import {
	ButtonColour,
	ButtonComponent,
	ButtonPriority,
	ButtonSize
} from '@vaad1st/component.atom.button';
import {
	IconActionCrossComponent,
	IconTravelPlaneComponent,
	IconTravelSuitcaseComponent
} from '@vaad1st/component.atom.icons';
import { ContainerComponent } from '@vaad1st/component.layout.container';
import {
	Suspense,
	lazy,
	useCallback,
	useEffect,
	type KeyboardEvent as ReactKeyboardEvent,
	type ReactNode,
	useMemo,
	useRef,
	useState
} from 'react';
import { useApplication } from '../../hooks/useApplication';
import { useBlanket } from '../../hooks/useBlanket';
import { useMediaQuery, BreakPointToken } from '@vaad1st/hook.media-query';
import { BookingType, BrandType, Config } from '../../types/config';
import { PRODUCT_TYPES } from '../../types/criteria';
import {
	BrandToggle,
	BrandToggleListItem,
	CloseButton,
	InnerPanel,
	SaleFlashOuter,
	Heading,
	SearchContainer,
	SearchExperience,
	BackgroundPanel,
	Header
} from './search-experience.styles';
import { bookingTypes, Brands } from '../../config/config';
import {
	TypographyComponent,
	TypographyVariant,
	TypographySize,
	TypographyWeight
} from '@vaad1st/component.atom.typography';
import triggerCustomEvent from '../../lib/utils/trigger-custom-event';
import { COOKIES, EVENTS } from '../../lib/utils/constants';
import {
	deleteCookie,
	getCookie,
	setSessionCookie
} from '../../lib/utils/feature-toggle';
import { useHolidayProductSelectorExperiment } from '../../experiments/holiday-product-selector/holiday-product-selector.experiment';

import {
	HOLIDAY_DESTINATION_FALLBACK,
	getDestinationPlaceholder,
	resolveDestinationLocale
} from '../../lib/utils/destination-placeholder.service';
import { VisuallyHidden } from '../forms/forms.styles';

const FlightOnlyComponent = lazy(() => import('../forms/flight.component'));
const HolidayComponent = lazy(() => import('../forms/holiday.component'));
const FlightCarComponent = lazy(() => import('../forms/flight-car.component'));
const MulticentreComponent = lazy(
	() => import('../forms/multicentre.component')
);
const CarComponent = lazy(() => import('../forms/car.component'));

const SearchTypeReady = ({
	children,
	onReady
}: {
	children: ReactNode;
	onReady: () => void;
}) => {
	useEffect(() => {
		onReady();
	}, [onReady]);

	return <>{children}</>;
};

interface SearchExperienceComponentProps {
	config: Config;
	shouldAnimate?: boolean;
	initialOpenState?: boolean;
	focusOnOpen?: boolean;
	editorId?: string;
}

const SearchExperienceComponent = ({
	config,
	shouldAnimate = false,
	initialOpenState = true,
	focusOnOpen = false,
	editorId
}: SearchExperienceComponentProps) => {
	const { application, setApplication } = useApplication();
	const { blanket, setBlanket } = useBlanket();

	const holidayProductSelectorExperiment =
		useHolidayProductSelectorExperiment();
	const isHolidayProductSelectorControl =
		holidayProductSelectorExperiment.variant === 'control';
	const [locale, setLocale] = useState<string>();

	const hasFlightPlusHotelABTestCookie =
		getCookie(COOKIES.FLIGHT_HOTEL) === '1';

	const defaultBookingType = useMemo(() => {
		return (
			config.bookingTypes.find((item) => {
				// AB Test: default to flight+hotel if the cookie is present
				if (
					(!application?.criteria?.searchType ||
						application?.criteria?.searchType === PRODUCT_TYPES.HOLIDAY) &&
					hasFlightPlusHotelABTestCookie
				) {
					return item.type === PRODUCT_TYPES.FLIGHT_HOTEL;
				}
				//

				return item.type === application?.criteria?.searchType;
			}) ?? config.bookingTypes[0]
		);
	}, [
		application?.criteria?.searchType,
		config.bookingTypes,
		hasFlightPlusHotelABTestCookie
	]);

	// Added for AB Test: Remove the cookie if not on flight+hotel to reset AB test
	useEffect(() => {
		if (defaultBookingType.type !== PRODUCT_TYPES.FLIGHT_HOTEL) {
			deleteCookie(COOKIES.FLIGHT_HOTEL);
		}
	}, [defaultBookingType.type]);
	//

	const [selectedBookingType, setSelectedBookingType] =
		useState<BookingType>(defaultBookingType);
	const [isHolidayProductSelectorActive, setIsHolidayProductSelectorActive] =
		useState<boolean>(() =>
			[
				PRODUCT_TYPES.HOLIDAY,
				PRODUCT_TYPES.MULTICENTRE,
				PRODUCT_TYPES.FLYDRIVE
			].includes(defaultBookingType.type)
		);
	const [isBlock, setIsBlock] = useState<boolean>(false);
	const [isShown, setIsShown] = useState<boolean>(false);
	const [searchTypeLoadingAnnouncement, setSearchTypeLoadingAnnouncement] =
		useState('');
	const topRef = useRef<HTMLParagraphElement | null>(null);
	const closeButtonRef = useRef<HTMLButtonElement | null>(null);
	const focusedForCurrentOpen = useRef(false);

	const isDesktop = useMediaQuery(`(min-width: ${BreakPointToken.MdMin}px)`);
	const isTablet = useMediaQuery(`(min-width: ${BreakPointToken.SmMin}px)`);

	const changeSelectedBookingType = useCallback((b: BookingType) => {
		setSelectedBookingType(b);
		// Added for AB Test: Persist that search initiated on flight+hotel tab
		setSessionCookie(
			COOKIES.FLIGHT_HOTEL,
			b.type === PRODUCT_TYPES.FLIGHT_HOTEL ? '1' : '0'
		);
		//
		triggerCustomEvent(EVENTS.searchPanelBrandChange, { bookingType: b.type });
	}, []);

	useEffect(() => {
		const unsupportedControlSelection =
			isHolidayProductSelectorActive &&
			isHolidayProductSelectorControl &&
			[PRODUCT_TYPES.HOTEL, PRODUCT_TYPES.CAR].includes(
				selectedBookingType.type
			);

		if (!unsupportedControlSelection) return;

		const holidayBookingType = config.bookingTypes.find(
			(bookingType) => bookingType.type === PRODUCT_TYPES.HOLIDAY
		);

		if (holidayBookingType) {
			changeSelectedBookingType(holidayBookingType);
		}
	}, [
		changeSelectedBookingType,
		config.bookingTypes,
		isHolidayProductSelectorActive,
		isHolidayProductSelectorControl,
		selectedBookingType.type
	]);

	const selectTopLevelBookingType = (bookingType: BookingType) => {
		const isHolidaySelection = bookingType.type === PRODUCT_TYPES.HOLIDAY;

		if (isHolidaySelection !== isHolidayProductSelectorActive) {
			setSearchTypeLoadingAnnouncement(
				`Loading ${isHolidaySelection ? 'Holidays' : 'Flights'} search`
			);
		}

		setIsHolidayProductSelectorActive(isHolidaySelection);
		changeSelectedBookingType(bookingType);
	};

	const announceSearchTypeReady = useCallback(() => {
		setSearchTypeLoadingAnnouncement((currentAnnouncement) => {
			if (currentAnnouncement === 'Loading Holidays search') {
				return 'Holidays search loaded';
			}

			if (currentAnnouncement === 'Loading Flights search') {
				return 'Flights search loaded';
			}

			return currentAnnouncement;
		});
	}, []);

	useEffect(() => {
		if (!searchTypeLoadingAnnouncement.endsWith('search loaded')) return;

		const announcementTimer = setTimeout(
			() => setSearchTypeLoadingAnnouncement(''),
			3000
		);

		return () => clearTimeout(announcementTimer);
	}, [searchTypeLoadingAnnouncement]);

	const selectHolidayBookingType = (bookingType: BookingType) => {
		setIsHolidayProductSelectorActive(true);
		changeSelectedBookingType(bookingType);
	};

	const mapIcon = (icon: string) => {
		switch (icon) {
			case 'icon-travel-plane':
				return IconTravelPlaneComponent;
			case 'icon-travel-suitcase':
				return IconTravelSuitcaseComponent;
		}
	};

	const closeExperience = () => {
		setBlanket({ ...blanket, show: false });
		setApplication((prev) => ({ ...prev, open: false }));
	};

	useEffect(() => {
		if (document) {
			triggerCustomEvent(EVENTS.searchPanelBrandChange, {
				bookingType: selectedBookingType.type
			});
		}
	}, []);

	useEffect(() => {
		setLocale(
			resolveDestinationLocale({
				cookieLocale: getCookie(COOKIES.NEXT_LOCALE),
				pathname: window.location.pathname,
				browserLocales:
					navigator.languages && navigator.languages.length
						? navigator.languages
						: [navigator.language]
			})
		);
	}, []);

	const flightDestinationPlaceholder =
		application.flightDestinationPlaceholder ??
		getDestinationPlaceholder(locale, 'flight');
	const holidayDestinationPlaceholder =
		application.holidayDestinationPlaceholder ??
		getDestinationPlaceholder(locale, 'holiday');

	useEffect(() => {
		let animationTimer: NodeJS.Timeout;
		let overflowTimer: NodeJS.Timeout;

		const { open } = application;

		const animateInTime = shouldAnimate ? 25 : 0;
		const animateOutTime = shouldAnimate ? 400 : 0;

		if (open) {
			setIsBlock(true);
			animationTimer = setTimeout(() => {
				setIsShown(true);

				if (focusOnOpen && !focusedForCurrentOpen.current) {
					(topRef.current ?? closeButtonRef.current)?.focus();
					focusedForCurrentOpen.current = true;
				}
			}, animateInTime);
		} else {
			focusedForCurrentOpen.current = false;
			setIsShown(false);
			animationTimer = setTimeout(() => setIsBlock(false), animateOutTime);
		}

		if (open) {
			// Set overflow hidden immediately when opening
			overflowTimer = setTimeout(() => {
				if (document) {
					const documentEl = document.documentElement;
					const hideOverflow = !isDesktop || (!isDesktop && !initialOpenState);

					documentEl.style.overflow = hideOverflow ? 'hidden' : '';
					document.body.style.overflow = hideOverflow ? 'hidden' : '';
				}
			}, animateInTime);
		} else {
			// Restore overflow after closing animation completes
			overflowTimer = setTimeout(() => {
				if (document) {
					document.documentElement.style.overflow = '';
					document.body.style.overflow = '';
				}
			}, animateOutTime);
		}

		return () => {
			clearTimeout(animationTimer);
			clearTimeout(overflowTimer);
		};
	}, [application, focusOnOpen, initialOpenState, isDesktop]);

	const showCloseButton =
		(application?.closable || !isTablet) && !application?.fullScreen;
	const isStandaloneHotelOrCar =
		!isHolidayProductSelectorActive &&
		[PRODUCT_TYPES.HOTEL, PRODUCT_TYPES.CAR].includes(selectedBookingType.type);
	const effectiveHideToggle =
		application.hideToggle === true || isStandaloneHotelOrCar;
	const handlePanelKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
		if (event.key !== 'Escape' || event.defaultPrevented || !showCloseButton)
			return;

		event.preventDefault();
		closeExperience();
	};

	return (
		<SearchExperience
			id={editorId}
			data-cy="search-experience-component"
			visible={isDesktop ? isBlock : isShown}
			animate={shouldAnimate}
			role="region"
			aria-label="Booking search"
			onKeyDown={handlePanelKeyDown}>
			<BackgroundPanel show={isShown} animate={shouldAnimate} />
			<ContainerComponent as="section">
				<InnerPanel
					show={isShown}
					animate={shouldAnimate}
					initiallyOpen={initialOpenState}>
					<Header>
						{!isStandaloneHotelOrCar && (
							<Heading className={effectiveHideToggle ? 'toggle-hidden' : ''}>
								{application.heading && (
									<TypographyComponent
										ref={topRef}
										variant={TypographyVariant.Heading}
										size={TypographySize.Mdm}
										as="p"
										tabIndex={-1}
										data-cy={'application-heading'}
										aria-label={`${application.heading}, begin your search...`}>
										{application.heading}
									</TypographyComponent>
								)}
							</Heading>
						)}

						{showCloseButton && (
							<CloseButton show={isShown}>
								<ButtonComponent
									ref={closeButtonRef}
									type="button"
									onClick={() => closeExperience()}
									priority={ButtonPriority.Tertiary}
									colour={ButtonColour.Inverse}
									size={ButtonSize.Small}
									aria-label="Close search panel"
									icon={IconActionCrossComponent}
									data-cy={'close-button'}
									// @ts-ignore
									tabIndex={isShown ? 0 : -1}
								/>
							</CloseButton>
						)}
					</Header>

					{!effectiveHideToggle && (
						<BrandToggle
							show={isShown}
							animate={shouldAnimate}
							aria-label="Search type">
							{Brands.map((brand: BrandType, idx: number) => {
								const isFlightPlusHotelTab =
									brand.defaultBookingType === PRODUCT_TYPES.FLIGHT_HOTEL;
								const isFlightPlusHotelSelected =
									selectedBookingType?.type === PRODUCT_TYPES.FLIGHT_HOTEL;
								const isActiveBrandTab = isFlightPlusHotelTab
									? isFlightPlusHotelSelected
									: brand.brand === selectedBookingType?.brand &&
										!isFlightPlusHotelSelected;

								// AB Test:
								// add the data attribute for AB test unless we have the cookie in which case we dont add it to make sure it shows
								const dataAttributes =
									isFlightPlusHotelTab &&
									!hasFlightPlusHotelABTestCookie &&
									!isFlightPlusHotelSelected
										? {
												'data-test-flight-plus-hotel-variant': true
											}
										: {};
								//
								return (
									<BrandToggleListItem
										{...dataAttributes}
										show={isShown}
										key={idx}
										data-testid="brand-toggle">
										{isActiveBrandTab && (
											<SaleFlashOuter id={`sales_flash_${brand?.brand}`}>
												<TypographyComponent
													variant={TypographyVariant.Body}
													size={TypographySize.Xsm}
													weight={TypographyWeight.Bold}
													as="span">
													<></>
												</TypographyComponent>
											</SaleFlashOuter>
										)}
										<ButtonComponent
											type="button"
											text={brand.displayName}
											aria-label={brand.displayName}
											size={ButtonSize.Medium}
											icon={brand.icon}
											priority={ButtonPriority.Selector}
											colour={ButtonColour.ContrastSecondary}
											isActive={isActiveBrandTab}
											aria-pressed={isActiveBrandTab}
											data-cy={`brand-toggle-${brand.brand}`}
											onClick={() => {
												const bookingType = bookingTypes.find(
													(b) => b.type === brand.defaultBookingType
												);

												if (!bookingType) return;

												selectTopLevelBookingType(bookingType);
											}}
											// @ts-ignore
											tabIndex={isShown ? 0 : -1}>
											{brand.displayName}
										</ButtonComponent>
									</BrandToggleListItem>
								);
							})}
						</BrandToggle>
					)}

					<VisuallyHidden
						role="status"
						aria-live="polite"
						aria-atomic="true"
						data-cy="search-type-loading-announcement">
						{searchTypeLoadingAnnouncement}
					</VisuallyHidden>

					<SearchContainer
						className={effectiveHideToggle ? 'toggle-hidden' : ''}
						show={isShown}
						animate={shouldAnimate}>
						<Suspense>
							{[
								PRODUCT_TYPES.FLIGHT_ONLY,
								PRODUCT_TYPES.REWARD_FLIGHT
							].includes(selectedBookingType.type) && (
								<SearchTypeReady onReady={announceSearchTypeReady}>
									<FlightOnlyComponent
										bookingType={selectedBookingType}
										destinationPlaceholder={flightDestinationPlaceholder}
									/>
								</SearchTypeReady>
							)}
							{[
								PRODUCT_TYPES.FLIGHT_HOTEL,
								PRODUCT_TYPES.HOLIDAY,
								PRODUCT_TYPES.HOTEL
							].includes(selectedBookingType.type) && (
								<SearchTypeReady onReady={announceSearchTypeReady}>
									<HolidayComponent
										bookingType={selectedBookingType}
										destinationPlaceholder={
											selectedBookingType.type === PRODUCT_TYPES.HOTEL
												? HOLIDAY_DESTINATION_FALLBACK
												: holidayDestinationPlaceholder
										}
										selectSubBookingType={
											isHolidayProductSelectorActive &&
											[PRODUCT_TYPES.HOLIDAY, PRODUCT_TYPES.HOTEL].includes(
												selectedBookingType.type
											)
												? selectHolidayBookingType
												: undefined
										}
									/>
								</SearchTypeReady>
							)}
							{selectedBookingType.type === PRODUCT_TYPES.FLYDRIVE && (
								<FlightCarComponent
									bookingType={selectedBookingType}
									selectSubBookingType={selectHolidayBookingType}
								/>
							)}
							{selectedBookingType.type === PRODUCT_TYPES.MULTICENTRE && (
								<MulticentreComponent
									bookingType={selectedBookingType}
									selectSubBookingType={selectHolidayBookingType}
								/>
							)}
							{selectedBookingType.type === PRODUCT_TYPES.CAR && (
								<CarComponent
									bookingType={selectedBookingType}
									selectSubBookingType={
										isHolidayProductSelectorActive
											? selectHolidayBookingType
											: undefined
									}
								/>
							)}
						</Suspense>
					</SearchContainer>
				</InnerPanel>
			</ContainerComponent>
		</SearchExperience>
	);
};

export default SearchExperienceComponent;
